import { FilterSidebar } from "@/components/layout/FilterSidebar";
import { NewSearchBar } from "@/components/layout/NewSearchBar";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import Seo from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { toArray } from "@/lib/array";
import {
  searchCategories as allSearchCategories,
  type SearchCategory,
} from "@/lib/searchCategories";
import { Article } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Clock, Filter, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

// 상단 카테고리 (market과 동일)
const marketCategories: SearchCategory[] = [
  allSearchCategories.find((c) => c.id === "community")!,
  allSearchCategories.find((c) => c.id === "community-talk")!,
  allSearchCategories.find((c) => c.id === "real-estate")!,
  allSearchCategories.find((c) => c.id === "used-car")!,
  allSearchCategories.find((c) => c.id === "job")!,
  allSearchCategories.find((c) => c.id === "meetup")!,
].filter(Boolean);

// 뉴스 카테고리
const newsCategories = [
  { id: "entertainment", label: "엔터테인먼트" },
  { id: "gaming", label: "게임" },
  { id: "sports", label: "스포츠" },
  { id: "politics", label: "정치" },
  { id: "economy", label: "경제" },
  { id: "investment", label: "투자" },
  { id: "realestate", label: "부동산" },
  { id: "lifestyle", label: "생활" },
  { id: "culture", label: "문화" },
  { id: "tech", label: "IT" },
  { id: "science", label: "과학" },
  { id: "immigration", label: "이민" },
  { id: "study", label: "유학" },
  { id: "column", label: "칼럼" },
  { id: "international", label: "국제" },
];

// 지역 필터
const regions = [
  { id: "ALL", label: "전체", country: null, state: null },
  { id: "KR", label: "한국", country: "KR", state: null },
  { id: "AU", label: "호주", country: "AU", state: null },
  { id: "NSW", label: "시드니 (NSW)", country: "AU", state: "NSW" },
  { id: "VIC", label: "멜번 (VIC)", country: "AU", state: "VIC" },
  { id: "QLD", label: "브리즈번/케언즈 (QLD)", country: "AU", state: "QLD" },
  { id: "SA", label: "아델레이드 (SA)", country: "AU", state: "SA" },
  { id: "WA", label: "퍼스 (WA)", country: "AU", state: "WA" },
  { id: "NT", label: "다윈 (NT)", country: "AU", state: "NT" },
  { id: "ACT", label: "캔버라 (ACT)", country: "AU", state: "ACT" },
];

export default function News() {
  const [, navigate] = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedRegion, setSelectedRegion] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [displayCount, setDisplayCount] = useState(25);

  // 카테고리 클릭 핸들러
  const handleCategoryClick = (category: SearchCategory) => {
    if (category.id === "community") {
      navigate("/market");
    } else if (category.id === "community-talk") {
      navigate("/community-talk");
    } else {
      navigate(`/market?category=${category.id}`);
    }
  };

  // 인기 검색어
  const getPopularKeywords = () => {
    return ["한호관계", "이민정책", "부동산", "경제", "투자", "K-컬처"];
  };

  // 기사 가져오기
  const { data: rawArticles, isLoading } = useQuery<Article[]>({
    queryKey: ["/api/articles", selectedCategory, selectedRegion],
    queryFn: async () => {
      const params = new URLSearchParams();
      // 카테고리 필터
      if (selectedCategory !== "all") {
        params.set("category", selectedCategory);
      }
      // 지역 필터
      // "전체"는 필터 없음 (한국과 호주 모두 표시)
      if (selectedRegion !== "ALL") {
        const regionInfo = regions.find((r) => r.id === selectedRegion);
        if (regionInfo) {
          // 국가 필터 (한국, 호주, 또는 특정 지역의 경우)
          if (regionInfo.country) {
            params.set("countryOrigin", regionInfo.country);
          }
          // 주/지역 필터 (특정 호주 지역만 선택한 경우)
          // "호주" 전체는 state 필터 없음 (모든 호주 지역 포함)
          if (regionInfo.state) {
            params.set("state", regionInfo.state);
          }
        }
      }
      params.set("limit", "100");
      params.set("locale", "ko");

      const response = await fetch(`/api/articles?${params}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch articles: ${response.status}`);
      }
      const data = await response.json();

      // Handle both array response and object response
      if (Array.isArray(data)) {
        return data;
      }
      if (data?.success && Array.isArray(data.data)) {
        return data.data;
      }
      if (data?.data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    },
  });

  // 필터 적용 (with safe array conversion)
  // Note: 카테고리와 지역 필터는 API에서 처리되므로, 여기서는 검색어 필터만 적용
  const articles = useMemo(() => {
    // Safe array conversion - prevents ".sort() is not a function" errors
    const allArticles = toArray<Article>(rawArticles);
    let filtered = allArticles;

    // 검색어 필터 (API에서 q 파라미터로도 처리 가능하지만, 클라이언트 사이드에서도 지원)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.titleKo?.toLowerCase().includes(searchLower) ||
          article.title?.toLowerCase().includes(searchLower) ||
          article.summaryKo?.toLowerCase().includes(searchLower) ||
          article.summary?.toLowerCase().includes(searchLower)
      );
    }

    // 최신순 정렬 (API에서도 정렬하지만, 클라이언트 사이드에서도 정렬 보장)
    return [...filtered].sort(
      (a, b) =>
        new Date(b.publishedAt || b.createdAt || 0).getTime() -
        new Date(a.publishedAt || a.createdAt || 0).getTime()
    );
  }, [rawArticles, searchTerm]);

  const latestPublishedAt = articles[0]?.publishedAt || articles[0]?.createdAt;
  const newsFeedIsStale = latestPublishedAt
    ? Date.now() - new Date(latestPublishedAt).getTime() > 24 * 60 * 60 * 1000
    : true;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto w-full max-w-[1536px] px-4 py-8 md:px-6 lg:px-8">
          <Skeleton className="h-10 w-48 mb-8" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title="뉴스 — OZKNEWS"
        description="호주 생활에 직접 영향을 주는 정책·지역·경제·한인사회 뉴스를 확인 가능한 원문 출처와 함께 한국어로 정리합니다."
        path="/news"
        keywords={[
          "호주 뉴스",
          "한국 뉴스",
          "교민 소식",
          "이민 정보",
          "한호 관계",
        ]}
      />

      <div className="min-h-screen bg-background">
        {/* Search Section - NewSearchBar 사용 */}
        <NewSearchBar
          defaultCategory="news"
          onSearch={(query) => {
            setSearchQuery(query);
            setSearchTerm(query);
          }}
        />

        {/* Main Content */}
        <PageContainer size="default">
          <div className="space-y-5">
            <div className="text-xs text-muted-foreground">홈 &gt; 뉴스</div>
            <PageHeader title={selectedRegion === "ALL" ? "전체 뉴스" : regions.find((r) => r.id === selectedRegion)?.label || "뉴스"} description="한국과 호주의 주요 소식을 한곳에서 빠르게 확인하세요." />
            {newsFeedIsStale && (
              <Card className="border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1 text-sm">
                    <p className="font-medium">뉴스 공급을 재정비하고 있습니다.</p>
                    <p className="text-xs opacity-80">
                      과거 기사는 당시 보도 시점의 기록입니다. 최신 생활 결정은 원문과 공식 기관 발표를 함께 확인해 주세요.
                    </p>
                  </div>
                </div>
              </Card>
            )}
            <div className="flex items-center justify-between gap-3 lg:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="lg:hidden"
                  data-testid="button-mobile-filter-toggle"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  필터
                </Button>
            </div>

            <div className="ozk-list-layout">
              {/* Sidebar - Filters (Desktop) - FilterSidebar 사용 */}
              <FilterSidebar
                sections={[
                  {
                    id: "region",
                    title: "지역뉴스",
                    type: "text",
                    options: regions.map((r) => ({
                      id: r.id,
                      label: r.label,
                      value: r.id,
                    })),
                    selected: selectedRegion,
                    onSelect: (value) => setSelectedRegion(value as string),
                  },
                  {
                    id: "category",
                    title: "카테고리",
                    type: "radio",
                    options: [
                      { id: "all", label: "전체", value: "all" },
                      ...newsCategories.map((c) => ({
                        id: c.id,
                        label: c.label,
                        value: c.id,
                      })),
                    ],
                    selected: selectedCategory,
                    onSelect: (value) => setSelectedCategory(value as string),
                  },
                ]}
                onReset={() => {
                  setSelectedRegion("ALL");
                  setSelectedCategory("all");
                }}
              />

              {/* Mobile Filters */}
              {showMobileFilters && (
                <div
                  className="fixed inset-0 z-[70] flex items-end bg-black/50 lg:hidden"
                  role="dialog"
                  aria-modal="true"
                  aria-label="뉴스 필터"
                  onClick={() => setShowMobileFilters(false)}
                >
                  <Card
                    className="max-h-[82vh] w-full overflow-y-auto rounded-b-none rounded-t-2xl border-b-0"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="p-6 space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className="font-semibold text-lg">필터</h2>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowMobileFilters(false)}
                          data-testid="button-close-mobile-filters"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* 지역 필터 */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">지역</h3>
                        <RadioGroup
                          value={selectedRegion}
                          onValueChange={setSelectedRegion}
                          className="space-y-1"
                        >
                          {regions.map((region) => (
                            <div
                              key={region.id}
                              className="flex items-center space-x-2"
                            >
                              <RadioGroupItem
                                value={region.id}
                                id={`region-${region.id}-mobile`}
                              />
                              <Label
                                htmlFor={`region-${region.id}-mobile`}
                                className="cursor-pointer text-sm font-normal leading-tight"
                              >
                                {region.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>

                      {/* 카테고리 필터 */}
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">카테고리</h3>
                        <RadioGroup
                          value={selectedCategory}
                          onValueChange={setSelectedCategory}
                          className="space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="all"
                              id="category-all-mobile"
                            />
                            <Label
                              htmlFor="category-all-mobile"
                              className="cursor-pointer text-sm font-normal leading-tight"
                            >
                              전체
                            </Label>
                          </div>
                          {newsCategories.map((category) => (
                            <div
                              key={category.id}
                              className="flex items-center space-x-2"
                            >
                              <RadioGroupItem
                                value={category.id}
                                id={`category-${category.id}-mobile`}
                              />
                              <Label
                                htmlFor={`category-${category.id}-mobile`}
                                className="cursor-pointer text-sm font-normal leading-tight"
                              >
                                {category.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                      <div className="sticky bottom-0 mt-4 grid grid-cols-2 gap-2 border-t bg-card pt-4">
                        <Button variant="outline" onClick={() => { setSelectedRegion("ALL"); setSelectedCategory("all"); }}>초기화</Button>
                        <Button onClick={() => setShowMobileFilters(false)}>결과 보기</Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Main List */}
              <main className="ozk-list-main">
                {/* Articles List */}
                {articles.length > 0 ? (
                  <>
                    <div className="space-y-0">
                      {articles.slice(0, displayCount).map((article) => {
                        const categoryInfo = newsCategories.find(
                          (c) => c.id === article.category
                        );

                        return (
                          <Link
                            key={article.id}
                            href={`/article/${article.id}`}
                            data-testid={`article-${article.id}`}
                          >
                            <div className="pb-4 border-b border-border/30 cursor-pointer transition-colors last:border-b-0">
                              <div className="flex gap-4">
                                {/* Thumbnail */}
                                {article.imageUrl && (
                                  <div className="flex-shrink-0">
                                    <img
                                      src={article.imageUrl}
                                      alt={article.titleKo || article.title}
                                      className="w-20 h-20 object-cover rounded-md"
                                    />
                                  </div>
                                )}

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Title */}
                                  <h3 className="font-semibold text-base mb-1 line-clamp-2 text-foreground">
                                    {article.titleKo || article.title}
                                  </h3>

                                  {/* Summary */}
                                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                    {article.summaryKo || article.summary}
                                  </p>

                                  {/* Meta Info - Category and Time */}
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {formatDistanceToNow(
                                        new Date(article.publishedAt),
                                        {
                                          addSuffix: true,
                                          locale: ko,
                                        }
                                      )}
                                    </div>
                                    {categoryInfo && (
                                      <>
                                        <span className="text-muted-foreground/60">
                                          ·
                                        </span>
                                        <span className="text-muted-foreground">
                                          {categoryInfo.label}
                                        </span>
                                      </>
                                    )}
                                    {article.author && (
                                      <>
                                        <span className="text-muted-foreground/60">
                                          ·
                                        </span>
                                        <span>{article.author}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    {/* 더보기 버튼 */}
                    {articles.length > displayCount && (
                      <div className="mt-6 text-center">
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={() => setDisplayCount(displayCount + 25)}
                        >
                          더보기 ({articles.length - displayCount}개 더)
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Card className="p-12 text-center">
                    <p className="text-muted-foreground">
                      {searchTerm
                        ? "검색 결과가 없습니다"
                        : "등록된 뉴스가 없습니다"}
                    </p>
                  </Card>
                )}
              </main>
            </div>
          </div>
        </PageContainer>
      </div>
    </>
  );
}
